import { Injectable, inject } from '@angular/core';

import { TODO } from '@b2b/types/core/utils';

import { CalendarService } from '../../calendar/calendar.service';
import { HelpersService } from '../../helpers/helpers.service';
import { UserPreferencesService } from '../../user-preferences/user-preferences.service';

/**
 * TemplatesHelpersService — Angular 18.
 *
 * Ported 1:1 from apps/b2b-ui/common/services/templates/helpers/helpers.service.js
 * (legacy AngularJS name 'TemplatesHelpersService', module
 * 'acme.common.service.templates.helpers').
 *
 * Stateless helper: repeat/sales-channel/flexpay constants, schedule date
 * formatting and template/ticket lookups. Consumed by TemplatesService,
 * FlexpayService and several template controllers/directives.
 *
 * Registered back into AngularJS under 'TemplatesHelpersService' via
 * downgradeInjectable in app/upgrade/ng-downgraded-components.ts.
 */
@Injectable({ providedIn: 'root' })
export class TemplatesHelpersService {
  private calendar = inject(CalendarService);
  private helpers = inject(HelpersService);
  private userPreferences = inject(UserPreferencesService);

  init(): boolean {
    return true;
  }

  getRepeatType(repeatTypes: TODO[], type: TODO): TODO {
    let r: TODO = false;

    if (repeatTypes instanceof Array) {
      for (let i = 0, ii = repeatTypes.length; i < ii; i++) {
        if (repeatTypes[i].value === type) {
          r = repeatTypes[i];
          break;
        }
      }
    }

    return r;
  }

  getAdmissionTypes(): TODO[] {
    return [
      { label: 'Standard', name: 'standard' },
      { label: 'General Admission', name: 'generalAdmission' },
    ];
  }

  getRepeatTypes(): TODO[] {
    return [
      { label: 'One-Time', value: 'never' },
      { label: 'Daily', value: 'day' },
      { label: 'Weekly', value: 'week' },
      { label: 'Monthly', value: 'month' },
    ];
  }

  getRepeatMonthTypes(): TODO[] {
    return [
      { label: 'on the', value: 'date' },
      { label: 'Every first', value: 'first' },
      { label: 'Every second', value: 'second' },
      { label: 'Every third', value: 'third' },
      { label: 'Every fourth', value: 'fourth' },
      { label: 'Every fifth', value: 'fifth' },
    ];
  }

  getSalesChannels(): TODO[] {
    return [
      { name: 'Web', channel: 'online', enabled: true },
      { name: 'Point of Sale', channel: 'pointOfSale', enabled: true },
      { name: 'Reseller', channel: 'reseller', enabled: true },
      { name: 'Customer Rep', channel: 'customerRep', enabled: true },
      { name: 'Manual Entry', channel: 'manualEntry', enabled: true },
      { name: 'Kiosk', channel: 'kiosk', enabled: true },
    ];
  }

  getFlexpayType(flexpayTypes: TODO[], type: TODO): TODO {
    let r: TODO = false;

    if (flexpayTypes instanceof Array) {
      for (let i = 0, ii = flexpayTypes.length; i < ii; i++) {
        if (flexpayTypes[i].value === type) {
          r = flexpayTypes[i];
          break;
        }
      }
    }

    return r;
  }

  getFlexpayTypes(): TODO[] {
    return [
      { label: 'Pay what you wish', value: 'payWhatYouWish' },
      { label: 'Discount', value: 'discount' },
    ];
  }

  getEffectiveDates(start: TODO, end: TODO): string {
    const startDate = this.calendar.parseDate(start);
    const endDate = end ? this.calendar.parseDate(end) : ' - Indefinite';

    return [startDate, endDate]
      .map((date: TODO) => this.calendar.toFormat(date, this.userPreferences.getDateFormat('shortDate', 'dateFormat')))
      .join(' - ');
  }

  getTimeFormat(): string {
    return this.userPreferences.getDateFormat('standardTime', 'hourFormat');
  }

  getResourceRequestTypes(resourceRequests: TODO): string {
    let entityName: string;
    const types: string[] = [];

    if (resourceRequests instanceof Array) {
      for (let i = 0, ii = resourceRequests.length; i < ii; i++) {
        entityName = this.helpers.cap(
          this.helpers.getEntityName(resourceRequests[i].eventResource.resourceCategory).displayPlural as string,
        );
        if (types.indexOf(entityName) === -1) {
          types.push(entityName);
        }
      }
    }

    return types.length ? types.join(', ') : 'None';
  }

  /**
   * Returns color category stylesheet for grid rows.
   */
  getColorCategoryStyle(colorCategory: TODO): Record<string, string> {
    let textColor = '#FFFFFF';
    let backgroundColor = '#000000';

    if (
      colorCategory.hasOwnProperty('textColor') &&
      typeof colorCategory.textColor === 'string' &&
      colorCategory.hasOwnProperty('backgroundColor') &&
      typeof colorCategory.backgroundColor === 'string'
    ) {
      textColor = colorCategory.textColor;
      backgroundColor = colorCategory.backgroundColor;
    }

    return {
      display: 'block',
      position: 'absolute',
      top: '0',
      left: '0',
      height: '100%',
      width: '4px',
      color: textColor,
      'background-color': backgroundColor,
    };
  }

  createHour(): TODO {
    return { hour: 9, minutes: 0, quantity: 1 };
  }

  createRange(): TODO {
    return {
      start: { hour: 9, minutes: 0 },
      end: { hour: 17, minutes: 0 },
    };
  }

  hourToDate(hour: TODO): Date {
    const d = new Date();

    if (hour) {
      d.setHours(hour.hour);
      d.setMinutes(hour.minutes);
      d.setSeconds(0);
      d.setMilliseconds(0);
    }

    return d instanceof Date ? d : new Date();
  }

  findTemplate(templates: TODO[], id: TODO): TODO {
    let r: TODO = false;

    for (let i = 0, ii = templates.length; i < ii; i++) {
      if (templates[i].id === id) {
        r = { template: templates[i], index: i };
        break;
      }
    }

    return r;
  }

  findTicketType(template: TODO, id: TODO): TODO {
    let r: TODO = false;

    if (template.priceLists instanceof Array && template.priceLists[0]) {
      if (template.priceLists[0].prices instanceof Array && template.priceLists[0].prices[0]) {
        for (let i = 0, ii = template.priceLists[0].prices.length; i < ii; i++) {
          if (template.priceLists[0].prices[i].personType.id === id) {
            r = {
              ticketType: template.priceLists[0].prices[i].personType,
              index: i,
            };
            break;
          }
        }
      }
    }

    return r;
  }
}
